/**
 * Cross-module X XWP-16 (02_DESIGN §3.2; brief §3.5, §3.6) — the grammar of the /magasins
 * workspace: URL keys, views, node ids, page sizes, sortable columns and form bounds. Every value
 * the server also states is its twin (`apps/server/src/config/placement-magasin.config.js`,
 * `validators/emplacement.validator.js`, `validators/warehouse.validator.js`), parity-tested in
 * `lib/__tests__/magasins.test.js`; the magasin types and statuses mirror the Prisma enums
 * `TypeMagasin` / `StatutMagasin` / `StatutTransfertStock` (same test). No business literal in
 * the screens (PK-BUG-15).
 */

/** Route of the workspace. `/emplacements` redirects to its « Emplacements » view (XMD-01-23). */
export const MAGASINS_ROUTE = "/magasins";

/** The three views (`?vue=`): the tree + detail, the flat emplacement list, the transfers. */
export const MAGASINS_VUES = Object.freeze({
  ENSEMBLE: "ensemble",
  EMPLACEMENTS: "emplacements",
  TRANSFERTS: "transferts",
});

/** URL keys of the workspace state (a link always reproduces what was seen). */
export const MAGASINS_PARAMS = Object.freeze({
  VUE: "vue",
  NOEUD: "noeud",
  ONGLET: "onglet",
  RECHERCHE: "q",
  PAGE: "page",
  FOCUS: "focus",
  PRODUIT_PANNEAU: "produit",
  LOT: "lot",
  PRODUIT_FILTRE: "produit_id",
  MAGASIN: "magasin_id",
  STATUT: "statut",
  TRANSFERT: "transfert",
});

/** Tabs of a magasin node's detail (the emplacements, every lot of the magasin, its breaches). */
export const ONGLETS_MAGASIN = Object.freeze({
  EMPLACEMENTS: "emplacements",
  LOTS: "lots",
  SEUILS: "seuils",
});

/** Node ids of the tree (`?noeud=`): `racine`, `m:<magasin>`, `e:<magasin>:<emplacement>`, `n:<magasin>`. */
export const NOEUD_RACINE = "racine";
export const PREFIXES_NOEUD = Object.freeze({ MAGASIN: "m", EMPLACEMENT: "e", NON_RANGE: "n" });

/** Twin of the server's HIERARCHIE_MAGASINS / NOEUD_TOUS_LOTS (the level-2 node names, bounds). */
export const HIERARCHIE = Object.freeze({
  noeud_non_range: "non_range",
  noeud_tous: "tous",
  lots_par_page: 50,
  emplacements_max: 1_000,
  recherche_max: 200,
});

/** Twin of the server's TRI_HIERARCHIE (sortable columns of the detail grids, defaults). */
export const TRI_HIERARCHIE = Object.freeze({
  emplacements: Object.freeze(["code", "zone", "rangement", "unites", "lots"]),
  emplacements_defaut: Object.freeze({ by: "code", order: "asc" }),
  lots: Object.freeze(["produit", "numero_lot", "date_peremption", "quantite", "alloue", "total", "vendable", "valeur"]),
  lots_defaut: Object.freeze({ by: "date_peremption", order: "asc" }),
});

/** Twin of the server's TRI_EMPLACEMENTS (`GET /emplacements`), with the page size of the view. */
export const TRI_EMPLACEMENTS = Object.freeze({
  cles: Object.freeze(["code", "zone", "magasin", "rangement"]),
  defaut: Object.freeze({ by: "code", order: "asc" }),
  limite: 50,
});

/** Twin of the server's LISTE_TRANSFERTS (`GET /transferts`). */
export const LISTE_TRANSFERTS = Object.freeze({
  limite: 50,
  tri: Object.freeze(["date", "numero", "statut", "source", "destination"]),
  defaut: Object.freeze({ by: "date", order: "desc" }),
  recherche_max: 50,
});

/** TypeMagasin, in display order (labels: server TYPE_MAGASIN_LABELS). PRINCIPAL is never chosen. */
export const TYPES_MAGASIN = Object.freeze([
  Object.freeze({ value: "PRINCIPAL", label: "Principal" }),
  Object.freeze({ value: "RESERVE", label: "Réserve" }),
  Object.freeze({ value: "COMPTOIR", label: "Comptoir" }),
  Object.freeze({ value: "AUTRE", label: "Autre" }),
]);
export const TYPE_MAGASIN_PRINCIPAL = "PRINCIPAL";
/**
 * X XWP-97 (XO-23 (7)) — a magasin of this type is always visible to the caissiers (server
 * `TYPE_MAGASIN_COMPTOIR`, config/disponibilite.config.js; the server refuses any other value, 422).
 */
export const TYPE_MAGASIN_COMPTOIR = "COMPTOIR";
export const TYPE_MAGASIN_DEFAUT = "RESERVE";

/** StatutMagasin / StatutTransfertStock values (labels and tones: config/status.config.js). */
export const STATUTS_MAGASIN = Object.freeze({ ACTIF: "ACTIF", FERME: "FERME" });
export const STATUTS_TRANSFERT = Object.freeze({ BROUILLON: "BROUILLON", VALIDE: "VALIDE", ANNULE: "ANNULE" });

/** Twin of the emplacement validator: lenient « AA-00 » code (XD-06, XMD-01-16) and field bounds. */
export const EMPLACEMENT_CHAMPS = Object.freeze({
  placeholder: "AA-00",
  motif: /^[A-Za-z]{1,4}-?\d{1,4}[A-Za-z]?$/,
  longueur_min: 2,
  longueur_max: 16,
  zone_max: 50,
  notes_max: 500,
});

/** Twin of the magasin validator's bounds (`warehouse.validator.js` magasinBody). */
export const MAGASIN_CHAMPS = Object.freeze({ code_max: 20, nom_max: 120, wilaya_max: 60, adresse_max: 300 });

/** Twin of the transfer and placement validators (lines, motif, quantity bound). */
export const TRANSFERT_CHAMPS = Object.freeze({ lignes_max: 200, motif_max: 300, quantite_max: 1_000_000 });
export const REPARTITION_CHAMPS = Object.freeze({ magasins_max: 50, emplacements_max: 200, quantite_max: 10_000_000, motif_max: 300 });

/** Options listed by the lot and emplacement pickers of the dialogs (remote search, first page). */
export const PICKERS_MAGASINS = Object.freeze({ lots: 20, emplacements: 20, produits: 10 });
